import { useEffect } from 'react';
import {
  normalizeTrackingName,
  trackCtaClick,
  trackEmailClick,
  trackPhoneClick,
} from '@/lib/utils/dataLayer';

const getTrackingLocation = (element: HTMLElement): string => {
  const section = element.closest('header, nav, main, footer, section, aside');

  if (section?.tagName.toLowerCase() === 'footer') return 'footer';
  if (section?.tagName.toLowerCase() === 'nav') return 'navbar';

  const sectionId = section?.getAttribute('id');
  if (sectionId) return sectionId;

  const pathname = window.location?.pathname || '/';
  return pathname === '/'
    ? 'homepage'
    : pathname.replace(/^\//, '') || 'homepage';
};

const getLinkLabel = (anchor: HTMLAnchorElement): string => {
  const explicitLabel = anchor.getAttribute('aria-label') || anchor.title;
  const visibleText = anchor.textContent || '';
  return normalizeTrackingName(explicitLabel || visibleText || anchor.href);
};

const isContactAnchor = (anchor: HTMLAnchorElement): boolean => {
  const href = anchor.getAttribute('href') || '';
  return href === '#contact-form' || href.endsWith('/#contact-form');
};

/**
 * Captures high-intent link clicks that are easy to miss when each page wires
 * analytics manually. This is intentionally limited to business contact links
 * and contact-form anchors so normal navigation analytics stays unchanged.
 */
const GlobalClickTracking = () => {
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const anchor = target.closest('a[href]');
      if (!(anchor instanceof HTMLAnchorElement)) return;

      const href = anchor.getAttribute('href') || '';
      const trackingLocation = getTrackingLocation(anchor);

      if (href.startsWith('tel:')) {
        trackPhoneClick(href.replace(/^tel:/, ''), {
          link_location: trackingLocation,
          link_text: getLinkLabel(anchor),
        });
        return;
      }

      if (href.startsWith('mailto:')) {
        const emailAddress = href
          .replace(/^mailto:/, '')
          .split('?')[0]
          .trim()
          .toLowerCase();

        if (emailAddress) {
          trackEmailClick(emailAddress, {
            link_location: trackingLocation,
            link_text: getLinkLabel(anchor),
          });
        }
        return;
      }

      if (isContactAnchor(anchor)) {
        trackCtaClick(getLinkLabel(anchor), trackingLocation, {
          destination: 'contact_form_section',
        });
      }
    };

    document.addEventListener('click', handleClick, { capture: true });
    return () => {
      document.removeEventListener('click', handleClick, { capture: true });
    };
  }, []);

  return null;
};

export default GlobalClickTracking;
